/**
 * Conversation Management Dialog
 * Multi-Chat System: Dialog for creating, editing, and deleting conversations
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { log } from '../../utils/errors/logger';

interface ConversationData {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview?: string;
}

interface ConversationManagementDialogProps {
  open: boolean;
  onClose: () => void;
  conversation?: ConversationData;
  mode: 'create' | 'edit' | 'delete';
  onSubmit: (data?: any) => void;
}

export const ConversationManagementDialog: React.FC<ConversationManagementDialogProps> = ({
  open,
  onClose,
  conversation,
  mode,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when dialog opens/closes or conversation changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && conversation) {
        setTitle(conversation.title);
      } else if (mode === 'create') {
        setTitle('');
      }
      
      log.component.mount('ConversationManagementDialog', { 
        mode, 
        conversationId: conversation?.id 
      });
    } else {
      setTitle('');
      setIsSubmitting(false);
    }
  }, [open, mode, conversation]);

  const handleSubmit = async () => {
    if (mode === 'delete') {
      setIsSubmitting(true);
      try {
        await onSubmit();
        log.user.action('conversation_delete_confirmed', 'ConversationManagementDialog', {
          conversationId: conversation?.id ?? null,
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (mode === 'create' || mode === 'edit') {
      if (!title.trim()) {
        return; // Don't submit empty titles
      }

      setIsSubmitting(true);
      try {
        await onSubmit({ title: title.trim() });
        log.user.action(`conversation_${mode}_submitted`, 'ConversationManagementDialog', {
          conversationId: conversation?.id ?? null,
          title: title.trim(),
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const getDialogTitle = () => {
    switch (mode) {
      case 'create':
        return t('conversation.create');
      case 'edit':
        return t('conversation.edit');
      case 'delete':
        return t('conversation.delete');
      default:
        return '';
    }
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case 'create':
        return t('common.create');
      case 'edit':
        return t('common.save');
      case 'delete':
        return t('common.delete');
      default:
        return t('common.submit');
    }
  };

  const getSubmitButtonColor = () => {
    return mode === 'delete' ? 'error' : 'primary';
  };

  const isSubmitDisabled = () => {
    if (isSubmitting) {return true;}
    if (mode === 'delete') {return false;}
    return !title.trim();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        timeout: 300,
      }}
      PaperProps={{
        sx: {
          minHeight: mode === 'delete' ? 'auto' : 200,
          borderRadius: 3,
          boxShadow: 6,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: mode === 'delete' ? 'error.main' : 'text.primary',
        }}
      >
        {getDialogTitle()}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {mode === 'delete' ? (
          <Box>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
              {t('conversation.deleteConfirmation')}
            </Typography>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'grey.100',
                border: 1,
                borderColor: 'grey.300',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>{conversation?.title}</strong>
              </Typography>
            </Box>
            {conversation?.message_count && conversation.message_count > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'warning.lighter',
                  border: 1,
                  borderColor: 'warning.light',
                }}
              >
                <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                  ⚠️ {t('conversation.deleteWarning', { count: conversation.message_count })}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <TextField
            autoFocus
            margin="dense"
            label={t('conversation.title')}
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={mode === 'create' ? t('conversation.titlePlaceholder') : ''}
            helperText={mode === 'create' ? t('conversation.titleHelper') : ''}
            disabled={isSubmitting}
            inputProps={{
              maxLength: 100,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 1,
                },
                '&.Mui-focused': {
                  boxShadow: 2,
                },
              },
            }}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          bgcolor: 'background.default',
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          color={getSubmitButtonColor() as any}
          variant="contained"
          disabled={isSubmitDisabled()}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          {isSubmitting ? t('common.submitting') : getSubmitButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};